import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1ed81136"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "crowdloan-contribute__header" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "crowdloan-contribute__title" }, "Contribute to fund", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "crowdloan-contribute__description" }, " Contribute to a crowd sale. It will be withdrawable when the crowdloan has ended and the funds are unused. ", -1))
const _hoisted_4 = { class: "crowdloan-contribute__info" }
const _hoisted_5 = { class: "crowdloan-contribute__info-item" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Minimum allowed", -1))
const _hoisted_7 = { class: "crowdloan-contribute__info-item" }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Remaining till cap", -1))

import WhiteWrapper from "@/components/white-wrapper/index.vue";
import ButtonsBlock from "@/components/buttons-block/index.vue";
import BaseButton from "@/components/base-button/index.vue";
import BackButton from "@/components/back-button/index.vue";
import AmountInput from "@/components/amount-input/index.vue";
import FeeInfo from "@/components/fee-info/index.vue";
import SelectAccountInput from "@/components/select-account-input/index.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Account } from "@/types/account";
import { CrowdloanInfo } from "@/types/crowdloan";
import {
  accounts,
  apiPromise,
  nativeBalances,
  nativeToken,
  selectedCrowdloan,
  selectedNetwork,
} from "@/stores";
import { GasFeeInfo } from "@/types/transaction";
import { useGetNativeBalances } from "@/libs/balances";
import { useGetNativePrice } from "@/libs/prices";
import { getSingleCrowdloanItem } from "@/utils/crowdloan";
import { fromBase, isValidDecimals, toBase } from "@/utils/units";
import { toBN } from "web3-utils";
import { getGasFeeInfo } from "@/utils/fee";
import { ParaId } from "@polkadot/types/interfaces";
import { crowdloanContributeExtrinsic } from "@/utils/extrinsic";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const fromAccount = ref<Account>(accounts.value[0]);
const amount = ref<number>(0);
const fee = ref<GasFeeInfo>();
const crowdloan = ref<CrowdloanInfo>();
const minContribution = ref(0);
const hasEnough = ref(true);

onMounted(() => {
  if (!selectedCrowdloan.value) {
    router.replace({ name: "crowdloan" });
  }

  if (route.query.address) {
    const found = accounts.value.find(
      (item) => item.address === route.query.address
    );

    if (found) {
      fromAccount.value = found;
    }
  }

  crowdloan.value = selectedCrowdloan.value;
  loadFeeInfo();
  useGetNativeBalances();
  useGetNativePrice();
  updateMinContribution();
  refreshSingleCrowdloan();
});

const refreshSingleCrowdloan = async () => {
  if (!selectedCrowdloan.value?.paraId) {
    return;
  }
  const api = await apiPromise.value;
  const result = await getSingleCrowdloanItem(
    api,
    selectedCrowdloan.value.paraId
  );
  crowdloan.value = result;
};

watch([accounts], () => {
  useGetNativeBalances();
  useGetNativePrice();
  fromAccount.value = accounts.value[0];
});

watch([selectedNetwork], () => {
  useGetNativeBalances();
  useGetNativePrice();
  refreshSingleCrowdloan();
  updateMinContribution();
});

watch(
  [amount, nativeBalances, fromAccount],
  async () => {
    if (!amount.value || !fromAccount.value?.address) {
      return;
    }

    if (!isValidDecimals(amount.value.toString(), nativeToken.value.decimals)) {
      hasEnough.value = false;
      return;
    }

    await apiPromise.value;

    const rawAmount = toBN(
      toBase(amount.value?.toString() || "0", nativeToken.value.decimals)
    );

    const rawBalance = toBN(
      toBase(
        nativeBalances[fromAccount.value.address]?.available.toString() || "0",
        nativeToken.value.decimals
      )
    );

    if (rawAmount.gt(rawBalance)) {
      hasEnough.value = false;
    } else {
      hasEnough.value = true;
    }
  },
  { deep: true }
);

const loadFeeInfo = async () => {
  // Load fee info for transaction with mock values
  const api = await apiPromise.value;

  const tx = await crowdloanContributeExtrinsic(
    api,
    api.createType<ParaId>("ParaId", 1),
    "1"
  );

  fee.value = await getGasFeeInfo(tx, fromAccount.value.address);
  console.log(fee.value);
};

watch([selectedNetwork, accounts], loadFeeInfo);

const minValueErrorMessage = computed(() => {
  if (minContribution.value > amount.value) {
    return `Minimum amount is ${
      minContribution.value
    } ${nativeToken.value.symbol.toLocaleUpperCase()}`;
  }
  return "";
});

const updateMinContribution = async () => {
  const api = await apiPromise.value;
  minContribution.value = Number(
    fromBase(
      api.consts.crowdloan?.minContribution?.toString() || "0",
      nativeToken.value.decimals
    )
  );
};

const isValid = computed<boolean>(() => {
  return (
    !!fromAccount.value &&
    Number(amount.value) > 0 &&
    hasEnough.value &&
    !minValueErrorMessage.value
  );
});

const availableBalance = computed(() => {
  if (!nativeBalances || !fromAccount.value) {
    return 0;
  }

  return nativeBalances[fromAccount.value.address]?.available.toNumber() || 0;
});

const nextAction = () => {
  selectedCrowdloan.value = crowdloan.value;
  router.push({
    name: "crowdloan-confirm",
    query: {
      address: fromAccount.value.address,
      amount: amount.value.toString(),
    },
  });
};

const back = () => {
  router.go(-1);
};

const inputAmount = (newVal: string) => {
  amount.value = Number(newVal);
};

const selectAccount = (account: Account) => {
  fromAccount.value = account;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(WhiteWrapper, { class: "crowdloan-contribute__wrap" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(BackButton, { action: back }),
        _hoisted_2
      ]),
      _hoisted_3,
      _createVNode(SelectAccountInput, {
        account: fromAccount.value,
        accounts: _unref(accounts),
        amount: availableBalance.value,
        "is-amount": true,
        title: "From",
        "onUpdate:select": selectAccount
      }, null, 8, ["account", "accounts", "amount"]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _hoisted_6,
          _createElementVNode("h5", null, [
            _createTextVNode(_toDisplayString(_ctx.$filters.cryptoCurrencyFormat(minContribution.value)), 1),
            _createElementVNode("span", null, _toDisplayString(_unref(nativeToken).symbol.toLocaleUpperCase()), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _hoisted_8,
          _createElementVNode("h5", null, [
            _createTextVNode(_toDisplayString(_ctx.$filters.formatCompactNumber(crowdloan.value?.remaining || 0)) + " ", 1),
            _createElementVNode("span", null, _toDisplayString(_unref(nativeToken).symbol.toLocaleUpperCase()), 1)
          ])
        ])
      ]),
      _createVNode(AmountInput, {
        "has-enough-balance": hasEnough.value,
        "is-error": !!minValueErrorMessage.value,
        token: _unref(nativeToken),
        value: String(amount.value),
        "max-value": _unref(nativeBalances)[fromAccount.value?.address]?.available,
        "inner-error-message": minValueErrorMessage.value,
        "onUpdate:amount": inputAmount
      }, null, 8, ["has-enough-balance", "is-error", "token", "value", "max-value", "inner-error-message"]),
      _createVNode(FeeInfo, { fee: fee.value }, null, 8, ["fee"]),
      _createVNode(ButtonsBlock, null, {
        default: _withCtx(() => [
          _createVNode(BaseButton, {
            title: "Continue",
            action: nextAction,
            send: true,
            disabled: !isValid.value
          }, null, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})